import { create } from 'zustand';

interface AuthUser {
  email: string;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => void;
}

const rawAuthUrl = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:4900';
const AUTH_API_URL = rawAuthUrl.replace(/\/+$/, '');

const safeParseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,

  checkAuth: () => {
    const token = localStorage.getItem('sa_access_token');
    const userJson = localStorage.getItem('sa_user');
    if (token && userJson) {
      const payload = safeParseJwt(token);
      if (payload && payload.exp && payload.exp * 1000 <= Date.now()) {
        // Token has actually expired — clean up
        localStorage.removeItem('sa_access_token');
        localStorage.removeItem('sa_user');
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const user = JSON.parse(userJson);
        set({ user, token, isAuthenticated: true, isLoading: false });
      } catch {
        localStorage.removeItem('sa_access_token');
        localStorage.removeItem('sa_user');
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
      }
    } else {
      set({ user: null, token: null, isAuthenticated: false, isLoading: false });
    }
  },

  login: async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('sa_access_token', data.token);
        localStorage.setItem('sa_user', JSON.stringify(data.user));
        set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  logout: () => {
    localStorage.removeItem('sa_access_token');
    localStorage.removeItem('sa_user');
    set({ user: null, token: null, isAuthenticated: false });
  },
}));
